import processEvent from 'analytics/processEvent';
import anaConsts from 'analytics/constants';
import anaUtils from 'analytics/utils';
import userUtils from 'utils/User';
import ViewAllLovesBindings from 'analytics/bindingMethods/pages/viewAllLovesBindings/ViewAllLovesBindings';
import { PRODUCT_RECOMMENDATION_ANALYTICS_CATEGORY } from 'ai/constants/superchat';

export function trackAddToBasket(skuId, options = {}) {
    const { useNextPageData = false } = options;

    const data = {
        pageName: anaConsts.SUPER_CHAT.SUPERCHAT_LANDING,
        actionInfo: anaConsts.SUPER_CHAT.SUPERCHAT_BASKET,
        eventStrings: 'scAdd',
        productStrings: `;${skuId};;;;eVar26=${skuId}|eVar23=superchat|eVar72=superchat|eVar133=standard`
    };

    if (useNextPageData) {
        anaUtils.setNextPageData(data);
    } else {
        processEvent.process(anaConsts.LINK_TRACKING_EVENT, { data });
    }
}

export function trackAddToLoves(skuId, product = {}) {
    const isAuthenticated = !userUtils.isAnonymous();

    if (isAuthenticated && product) {
        const sku = {
            productId: product.id,
            productName: product.title,
            listPrice: product.list_price,
            brandName: product.brand_name,
            categoryName: '',
            variationType: '',
            variationValue: '',
            isNew: product.isNew,
            isOutOfStock: false
        };
        ViewAllLovesBindings.triggerShareableListSOTAnalytics('love', skuId, sku, []);
    }

    processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
        data: {
            pageName: anaConsts.SUPER_CHAT.SUPERCHAT_LANDING,
            eventStrings: anaConsts.Event.EVENT_27,
            productStrings: `;${skuId};;;;eVar26=${skuId}`
        }
    });
}

export function trackRemoveFromLoves(skuId, product = {}) {
    const isAuthenticated = !userUtils.isAnonymous();

    if (isAuthenticated && product) {
        const sku = {
            productId: product.id,
            productName: product.title,
            listPrice: product.list_price,
            brandName: product.brand_name,
            categoryName: '',
            variationType: '',
            variationValue: '',
            isNew: product.isNew,
            isOutOfStock: false
        };
        ViewAllLovesBindings.triggerShareableListSOTAnalytics('un-love', skuId, sku, []);
    }

    processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
        data: {
            pageName: anaConsts.SUPER_CHAT.SUPERCHAT_LANDING,
            eventStrings: anaConsts.Event.EVENT_28,
            productStrings: `;${skuId};;;;eVar26=${skuId}`
        }
    });
}

export function trackPurchaseHistoryCardView() {
    processEvent.process(anaConsts.ASYNC_PAGE_LOAD, {
        data: {
            pageName: anaConsts.SUPER_CHAT.SUPERCHAT_PURCHASE_HISTORY
        }
    });
}

export function trackPurchaseHistoryAddToBasket(skuId, fulfillmentType = 'standard') {
    processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
        data: {
            pageName: anaConsts.SUPER_CHAT.SUPERCHAT_PURCHASE_HISTORY,
            actionInfo: anaConsts.SUPER_CHAT.SUPERCHAT_PURCHASE_HISTORY_BASKET,
            eventStrings: 'scAdd',
            productStrings: `;${skuId};;;;eVar26=${skuId}|eVar23=superchat|eVar72=superchat|eVar133=${fulfillmentType}`
        }
    });
}

/**
 * Fires an Adobe Analytics async page load (s.t() / TrackState) when a SuperChat
 * Loyalty card is rendered. Sets `pageName` to the loyalty page identifier per AC1
 * of GAI-6110. Should be called once per card render (caller is responsible for guarding).
 *
 * @param {('birthday'|'rewardsBazaar')} rewardType - Reward card type. Drives `pageName`:
 *   `birthday` => `superchat:loyalty:birthday gift:*`,
 *   anything else => `superchat:loyalty:reward bazaar:*`.
 * @returns {void}
 */
export function trackRewardCardView(rewardType) {
    const pageName = rewardType === 'birthday' ? anaConsts.SUPER_CHAT.SUPERCHAT_BIRTHDAY_REWARD : anaConsts.SUPER_CHAT.SUPERCHAT_REWARDS_BAZAAR;

    processEvent.process(anaConsts.ASYNC_PAGE_LOAD, {
        data: { pageName }
    });
}

/**
 * Fires an Adobe Analytics link tracking event (s.tl() / TrackAction) when a user
 * adds a Loyalty card item to basket. Implements AC2 of GAI-6110.
 *
 * Adobe variables set:
 *   - pageName / eVar70 / eVar97: loyalty page identifier (same as card view)
 *   - prop55 (actionInfo): `reward bazaar:add-to-basket` (intentionally identical for both
 *     reward types — confirmed with analytics owner)
 *   - events: `scAdd`
 *   - products: `;<sku>;;;;eVar26=<sku>|eVar23=superchat|eVar133=standard|eVar72=superchat`
 *   - eVar61 (biRewardType): the reward identifier (e.g., `100 Points` or `Birthday Gift`)
 *
 * @param {string} skuId - SKU id of the reward item being added.
 * @param {('birthday'|'rewardsBazaar')} rewardType - Reward card type that fired the event.
 * @param {string} biRewardType - Reward identifier passed to eVar61
 *   (e.g., `100 Points` for Reward Bazaar, `Birthday Gift` for Birthday).
 * @returns {void}
 */
export function trackRewardAddToBasket(skuId, rewardType, biRewardType) {
    const isBirthday = rewardType === 'birthday';

    processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
        data: {
            pageName: isBirthday ? anaConsts.SUPER_CHAT.SUPERCHAT_BIRTHDAY_REWARD : anaConsts.SUPER_CHAT.SUPERCHAT_REWARDS_BAZAAR,
            actionInfo: isBirthday ? anaConsts.SUPER_CHAT.SUPERCHAT_BIRTHDAY_REWARD_BASKET : anaConsts.SUPER_CHAT.SUPERCHAT_REWARDS_BAZAAR_BASKET,
            eventStrings: 'scAdd',
            productStrings: `;${skuId};;;;eVar26=${skuId}|eVar23=superchat|eVar133=standard|eVar72=superchat`,
            biRewardType
        }
    });
}

export function buildBasketAnalyticsData(sku, options = {}) {
    const { sotV425 = null } = options;

    return {
        productId: sku.productId,
        brandName: sku.brand_name,
        productName: sku.title,
        category: PRODUCT_RECOMMENDATION_ANALYTICS_CATEGORY,
        pageName: anaConsts.SUPER_CHAT.SUPERCHAT_LANDING,
        customActionInfo: anaConsts.SUPER_CHAT.SUPERCHAT_BASKET,
        productStrings: [`;${sku.skuId};;;;eVar26=${sku.skuId}|eVar23=superchat|eVar133=standard`],
        fromSuperChatModal: true,
        ...(sotV425 && { sotV425 })
    };
}
